import type {
  AdjustmentDocument,
  AdjustmentExistingDocument,
  AdjustmentExistingDocumentRaw,
  Column,
  ColumnKey,
  FormError,
  SubAdjustmentRow,
  SubAdjustmentRowAccount,
  SubAdjustmentRowRaw,
  Tag,
} from '../types'
import { isExistingAttachment, isMathExpressionColumn } from '../predicates'
import type { Blob as ActiveBlob } from '@rails/activestorage'

import humps from 'humps'
import pick from 'lodash/pick'
import mapValues from 'lodash/mapValues'
import { defineStore } from 'pinia'
import { v4 as generateUuid } from 'uuid'
import { computed, ref } from 'vue'

import { emptySubAdjustment } from '../constants'
import { formatSubadjustmentData, parseExpressiveValue } from '../utils'

export const useAdjustmentsModalStore = defineStore('adjustmentsModal', () => {
  const attachmentObjects = ref<Record<string, AdjustmentDocument>>({})
  const attachmentOrder = ref<string[]>([])
  const deletedAttachments = ref<AdjustmentExistingDocument[]>([])

  const isAnyAttachmentUploading = computed(() => {
    return Object.values(attachmentObjects.value).some(
      (attachment) =>
        !isExistingAttachment(attachment) && !attachment.uploadPath
    )
  })

  function addAttachment({
    blob,
    uuid,
  }: {
    blob: ActiveBlob
    uuid: string
  }): void {
    attachmentObjects.value[uuid] = {
      id: uuid,
      filename: blob.filename,
      type: blob.content_type,
      size: blob.byte_size,
      uploadPath: blob.signed_id,
      new: true,
    }
  }

  function receiveAttachments(docs: AdjustmentExistingDocumentRaw[]): void {
    docs.forEach((doc) => {
      attachmentOrder.value.push(doc.id.toString())
      attachmentObjects.value[doc.id.toString()] = {
        id: doc.id,
        filename: doc.documentUserFileName || doc.documentFileName,
        new: false,
      }
    })
  }

  function removeAttachment(id: string | number): void {
    const attachmentToDelete = attachmentObjects.value[id]

    if (attachmentToDelete && isExistingAttachment(attachmentToDelete)) {
      deletedAttachments.value.push(attachmentToDelete)
    }

    delete attachmentObjects.value[id]

    const attachmentIndex = attachmentOrder.value.findIndex(
      (existingId) => existingId === id.toString()
    )

    attachmentOrder.value.splice(attachmentIndex, 1)
  }

  function startFileUpload({ uuid, file }: { uuid: string; file: File }): void {
    attachmentObjects.value[uuid] = {
      id: uuid,
      filename: file.name,
      type: file.type,
      size: file.size,
      uploadPath: '',
      new: true,
    }

    attachmentOrder.value.push(uuid)
  }

  const uploadProgress = ref<Record<string, string>>({})

  function updateUploadProgress({
    uuid,
    progress,
  }: {
    uuid: string
    progress: string
  }): void {
    uploadProgress.value = {
      ...uploadProgress.value,
      [uuid]: progress,
    }
  }

  const category = ref<string>('')

  function receiveCategories(cat: string): void {
    category.value = cat
  }

  const columns = ref<Column[]>([])

  const focusedColumn = ref<{
    id: string
    column: ColumnKey | null
    timestamp: number
  }>({
    id: '',
    column: null,
    timestamp: 0,
  })

  const editedColumnsPerRow = ref<
    Record<string, Record<keyof SubAdjustmentRow, boolean>>
  >({})

  function focusColumn({
    id,
    column,
  }: {
    id: string
    column: ColumnKey
  }): void {
    focusedColumn.value = {
      id,
      column,
      timestamp: Date.now(),
    }
  }

  function markColumnEdited(
    rowId: string,
    column: keyof SubAdjustmentRow
  ): void {
    if (!editedColumnsPerRow.value[rowId]) {
      editedColumnsPerRow.value[rowId] = {} as Record<
        keyof SubAdjustmentRow,
        boolean
      >
    }

    editedColumnsPerRow.value[rowId][column] = true
  }

  function receiveColumns(incomingColumns: Column[]): void {
    columns.value = incomingColumns.map((column) => ({
      ...column,
      key: humps.camelize(column.key),
    }))
  }

  const adjustmentErrors = computed(() => {
    const errors = []

    if (!isBalanced.value) {
      errors.push(window.I18n.t('adjustment.modal.errors.unbalanced'))
    }

    const anyWithEmptyValues = subAdjustmentOrder.value.some((id) => {
      if (emptySubAdjustmentRows.value[id]) {
        return false
      }

      const numericDebit = parsedValuesbyAdjustmentId.value[id].valueDebit
      const numericCredit = parsedValuesbyAdjustmentId.value[id].valueCredit

      if (!numericDebit && !numericCredit) {
        return true
      }

      return false
    })

    if (anyWithEmptyValues) {
      errors.push(
        window.I18n.t('adjustment.modal.errors.non_zero_sub_adjustments')
      )
    }

    return errors
  })

  const errors = computed<Record<string, FormError>>(() => {
    return subAdjustmentOrder.value.reduce((acc, id) => {
      if (emptySubAdjustmentRows.value[id]) {
        return acc
      }

      const adjustment = subAdjustments.value[id]

      const erroObj = requiredColumns.value.reduce((acc, column) => {
        // We do not care about the computed adjusted value and prefill the actual value with 0 if it is not set
        if (column.key === 'actualValue' || column.key === 'adjustedValue') {
          return acc
        }

        const value = adjustment[column.key]

        // Special handling for the account column as it contains an object with empty fields initially
        if (column.key === 'account') {
          const accountValue = (value ?? {}) as SubAdjustmentRowAccount
          const isEmptyAccount = !accountValue.number || !accountValue.name

          if (isEmptyAccount) {
            acc[column.key] = window.I18n.t('adjustment.modal.errors.required')
          }

          return acc
        }

        if (!value) {
          acc[column.key] = window.I18n.t('adjustment.modal.errors.required')
        }

        return acc
      }, {} as FormError)

      if (Object.keys(erroObj).length === 0) {
        return acc
      }

      acc[id] = erroObj
      return acc
    }, {} as Record<string, FormError>)
  })

  const serverErrors = ref<Array<string>>([])

  const shouldDisplayErrors = computed(() => {
    return submitTimestamp.value > 0
  })

  const hasErrors = computed(() => {
    return (
      Object.keys(errors.value).length > 0 || adjustmentErrors.value.length > 0
    )
  })

  const requiredColumns = computed(() => {
    return columns.value.filter((column) => column.required)
  })

  const existingTags = ref<Tag[]>([])
  const selectedTags = ref<number[]>([])

  function receiveTags({ previousTags }: { previousTags: Tag[] }): void {
    existingTags.value = previousTags
    selectedTags.value = previousTags.map((tag) => tag.id)
  }

  const subAdjustments = ref<Record<string, SubAdjustmentRow>>({})
  const subAdjustmentOrder = ref<string[]>([])
  const originalAccountBalances = ref<Record<string, number>>({})

  const emptySubAdjustmentRows = computed(() => {
    return subAdjustmentOrder.value.reduce((acc, subId) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id, account, ...rest } = subAdjustments.value[subId]

      const allValuesEmpty =
        Object.values(rest).every((value) => !value) &&
        Object.values(account || {}).every((value) => !value)

      if (allValuesEmpty) {
        acc[subId] = true
      }

      return acc
    }, {} as Record<string, boolean>)
  })

  const convertMathExpressionToNumber = (
    id: string | number,
    column: keyof SubAdjustmentRow
  ): void => {
    if (!isMathExpressionColumn(column)) {
      return
    }

    const result = parsedValuesbyAdjustmentId.value?.[id]?.[column]

    if (!result && result !== 0) {
      return
    }

    subAdjustments.value[id][column] = result.toFixed(2).toString()
  }

  const lastValidDescription = computed(() => {
    const reversedOrder = subAdjustmentOrder.value.slice().reverse()

    const id = reversedOrder.find(
      (subId) => !!subAdjustments.value[subId].description
    )

    if (!id) {
      return ''
    }

    return subAdjustments.value[id]?.description ?? ''
  })

  const subAdjustmentIdsByAccountId = computed(() => {
    return Object.values(subAdjustments.value).reduce((acc, subAdjustment) => {
      const account = subAdjustment.account

      if (!account) {
        return acc
      }

      const accountId = account.id

      if (!accountId) {
        return acc
      }

      if (!acc[accountId]) {
        acc[accountId] = []
      }

      acc[accountId].push(subAdjustment.id.toString())

      return acc
    }, {} as Record<number, string[]>)
  })

  const parsedValuesbyAdjustmentId = computed(() => {
    return subAdjustmentOrder.value.reduce((acc, id) => {
      const adjustment = subAdjustments.value[id]

      // Users may also input simple math formulas in the debit/credit fields other than plain numbers
      const mappedAdjustment = mapValues(
        pick(adjustment, ['valueDebit', 'valueCredit']),
        parseExpressiveValue
      )

      acc[id] = mappedAdjustment

      return acc
    }, {} as Record<string, { valueDebit: number; valueCredit: number }>)
  })

  const totals = computed(() => {
    return subAdjustmentOrder.value.reduce(
      (acc, id) => {
        const newDebitTotal =
          acc[0] + parsedValuesbyAdjustmentId.value[id].valueDebit
        const newCreditTotal =
          acc[1] + parsedValuesbyAdjustmentId.value[id].valueCredit

        return [
          parseFloat(newDebitTotal.toFixed(2)),
          parseFloat(newCreditTotal.toFixed(2)),
        ]
      },
      [0, 0]
    )
  })

  const isBalanced = computed(() => {
    const [debit, credit] = totals.value
    return debit === credit
  })

  function addEmptySubAdjustment(): void {
    const newId = generateUuid()

    subAdjustments.value = {
      ...subAdjustments.value,
      [newId]: {
        ...emptySubAdjustment,
        id: newId,
      },
    }
    subAdjustmentOrder.value.push(newId)
  }

  function addSubAdjustment(adjustment: SubAdjustmentRow): void {
    const id = adjustment.id.toString()

    const previousAdjustment =
      subAdjustments.value[
        subAdjustmentOrder.value[subAdjustmentOrder.value.length - 1]
      ]

    subAdjustmentOrder.value.push(id)
    subAdjustments.value[id] = {
      ...adjustment,
      description: previousAdjustment.description,
    }
  }

  function deleteSubAdjustment(id: string | number): void {
    delete subAdjustments.value[id]
    subAdjustmentOrder.value = subAdjustmentOrder.value.filter(
      (subId) => subId !== id.toString()
    )
  }

  function prefillAdjustment(id: string): void {
    if (!subAdjustments.value[id]) {
      return
    }

    subAdjustments.value[id].description =
      subAdjustments.value[id].description || lastValidDescription.value
  }

  const isLoading = ref<boolean>(false)
  const isLocked = ref<boolean>(false)

  function receiveSubAdjustments(
    adjustments: SubAdjustmentRowRaw[],
    {
      shouldCalculateBalance,
    }: {
      shouldCalculateBalance: boolean
    }
  ): void {
    const {
      originalAccountBalances: existingOriginalAccountBalances,
      subAdjustments: existingAdjustments,
      subAdjustmentOrder: existingOrder,
    } = formatSubadjustmentData(adjustments, {
      shouldCalculateBalance,
    })

    originalAccountBalances.value = existingOriginalAccountBalances
    subAdjustments.value = existingAdjustments
    subAdjustmentOrder.value = existingOrder
  }

  const accountEntryId = ref<number | undefined>(undefined)
  const lastOpenedAdjustmentId = ref<string | undefined>(undefined)
  const ledgerId = ref<number>(0)
  const submitTimestamp = ref<number>(0)

  function resetStore(): void {
    attachmentObjects.value = {}
    attachmentOrder.value = []
    deletedAttachments.value = []
    uploadProgress.value = {}
    category.value = ''
    existingTags.value = []
    editedColumnsPerRow.value = {}
    selectedTags.value = []
    serverErrors.value = []
    subAdjustments.value = {}
    subAdjustmentOrder.value = []
    submitTimestamp.value = 0
  }

  return {
    attachmentObjects,
    attachmentOrder,
    deletedAttachments,
    uploadProgress,
    isAnyAttachmentUploading,
    addAttachment,
    receiveAttachments,
    removeAttachment,
    startFileUpload,
    updateUploadProgress,
    category,
    receiveCategories,
    columns,
    editedColumnsPerRow,
    focusedColumn,
    focusColumn,
    markColumnEdited,
    receiveColumns,
    adjustmentErrors,
    errors,
    serverErrors,
    hasErrors,
    shouldDisplayErrors,
    previousTags: existingTags,
    selectedTags,
    receiveTags,
    originalAccountBalances,
    subAdjustments,
    subAdjustmentOrder,
    subAdjustmentIdsByAccountId,
    emptySubAdjustmentRows,
    isBalanced,
    parsedValuesbyAdjustmentId,
    totals,
    addEmptySubAdjustment,
    addSubAdjustment,
    convertMathExpressionToNumber,
    deleteSubAdjustment,
    prefillAdjustment,
    receiveSubAdjustments,
    accountEntryId,
    isLoading,
    isLocked,
    lastOpenedAdjustmentId,
    ledgerId,
    submitTimestamp,
    resetStore,
  }
})
